import type { InputHTMLAttributes, ReactNode } from "react";
import { Check } from "lucide-react";
import { Icon } from "./Icon";
import { cx } from "./cx";
import { VISUALLY_HIDDEN_INPUT } from "./fieldParts";

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: ReactNode;
  hint?: string;
}

/** Square checkbox, green when checked. For form submission; use Switch for instant effect. */
export function Checkbox({ label, hint, className, ...rest }: CheckboxProps) {
  return (
    <label className={cx("group relative flex cursor-pointer items-start gap-3 has-disabled:cursor-not-allowed", className)}>
      <input type="checkbox" className={VISUALLY_HIDDEN_INPUT} {...rest} />
      {/* The box follows the native input's state, so controlled and uncontrolled use both work. */}
      <span
        className={cx(
          "mt-px flex size-5 flex-none items-center justify-center rounded-xs border text-white",
          "border-border-strong bg-surface-card inset-shadow-default [transition:var(--transition-control)]",
          "peer-checked:border-green-700 peer-checked:bg-green-700 peer-checked:inset-shadow-none",
          "peer-disabled:border-paper-3 peer-disabled:bg-paper-2 peer-disabled:inset-shadow-none",
          "peer-focus-visible:shadow-focus",
          "[&>svg]:invisible peer-checked:[&>svg]:visible",
        )}
      >
        <Icon icon={Check} size={14} strokeWidth={2.5} />
      </span>
      <span>
        <span className="block type-body [font-size:var(--text-base)] text-text-body group-has-disabled:text-ink-300">{label}</span>
        {hint && <span className="block type-small text-text-muted">{hint}</span>}
      </span>
    </label>
  );
}
